import { useSelector } from "react-redux";
import { validators } from "investira.sdk";

const xTipoAcesso = [
  { id: "null", value: null, label: "Nenhum" },
  { id: "C", value: "C", label: "Consulta" },
  { id: "E", value: "E", label: "Editar" },
  { id: "G", value: "G", label: "Gerencia" }, // E/G são a mesma coisa
  { id: "F", value: "F", label: "Configuração" },
  { id: "A", value: "A", label: "Aprovação" },
];

// Chaves de regras de acesso
const xAcessosKeys = [
  "RLENQ", // Enquadramento
  "RLVAR", // Risco
  "RLDUR", // Duration
  "RLSTT", // Stress Test
  "RLCNC", // Conferência Custodiante
  "RLBCK",
  "RLCRL",
  "RLFEF",
];

function useAcesso() {
  const { usuario_id: xUsuarioId } = useSelector((store) => store.user);

  // Pega data e current da entidade do store redux
  const { data: xData, current: xCurrent } = useSelector(
    (store) => store.entidade
  );

  // Busca a entidade ativa
  const xEntidade = Array.isArray(xData)
    ? xData.find(
        (xEntidade) => xEntidade.entidade_id === xCurrent.default_entidade_id
      )
    : null;

  // Acessos atuais do usuário na entidade ativa
  const xCurrentAcessos =
    xCurrent.membros &&
    xCurrent.membros.find((xMembro) => xMembro.usuario_id === xUsuarioId)
      .acessos
      ? { ...xEntidade.acessos }
      : xAcessosKeys.reduce((xAcc, xKey) => ({ ...xAcc, [xKey]: null }), {});

  // Função para checar o valor de acesso de um recurso específico
  const canAcessar = (pAcessoKey, pMinLevel = "C") => {
    // Retorna false se não tiver esse acesso definido
    if (!pAcessoKey || validators.isEmpty(xCurrentAcessos[pAcessoKey]))
      return false;

    // Define prioridade dos níveis de acesso
    const xPriority = { null: 0, C: 1, E: 2, G: 2, F: 3, A: 4 };
    const xUserLevel = xCurrentAcessos[pAcessoKey];
    // Caso o nível mínimo for null, qualquer valor é suficiente
    if (pMinLevel === null) return !validators.isNull(xUserLevel);
    return xPriority[xUserLevel] >= xPriority[pMinLevel];
  };

  // Retorna todos os acessos atuais, útil para renderização e updates
  const getAcessos = () => ({
    ...xCurrentAcessos,
  });

  // Função utilitária: retorna todos acessos do tipo especificado (por ex, "C", "E", "G" "A")
  const hasAlgumAcesso = (pLevel) => {
    return xAcessosKeys.some((xKey) => xCurrentAcessos[xKey] === pLevel);
  };

  // Exemplo: "RLENQ" para Edição

  const podeEditar = (pAcessoKey) =>
    canAcessar(pAcessoKey, "G") || canAcessar(pAcessoKey, "E");
  const podeAprovar = (pAcessoKey) => canAcessar(pAcessoKey, "A");
  const podeConsultar = (pAcessoKey) => canAcessar(pAcessoKey, "C");
  const podeGerenciar = (pAcessoKey) => canAcessar(pAcessoKey, "G");
  const podeConfigurar = (pAcessoKey) => canAcessar(pAcessoKey, "F");

  const podeEditarAprovar = (pAcessoKey) =>
    podeEditar(pAcessoKey) || podeAprovar(pAcessoKey);

  const podeGerenciarAprovar = (pAcessoKey) =>
    podeGerenciar(pAcessoKey) || podeAprovar(pAcessoKey);

  const podeEditarGerenciarAprovar = (pAcessoKey) =>
    podeEditar(pAcessoKey) ||
    podeGerenciar(pAcessoKey) ||
    podeAprovar(pAcessoKey);

  const podeConsultarEditarAprovar = (pAcessoKey) =>
    podeConsultar(pAcessoKey) ||
    podeEditar(pAcessoKey) ||
    podeAprovar(pAcessoKey);

  const podeConsultarEditarGerenciarAprovar = (pAcessoKey) =>
    podeConsultar(pAcessoKey) ||
    podeEditar(pAcessoKey) ||
    podeGerenciar(pAcessoKey) ||
    podeAprovar(pAcessoKey);

  const podeConsultarEditarGerenciarConfigurarAprovar = (pAcessoKey) =>
    podeConsultar(pAcessoKey) ||
    podeEditar(pAcessoKey) ||
    podeGerenciar(pAcessoKey) ||
    podeConfigurar(pAcessoKey) ||
    podeAprovar(pAcessoKey);

  // Exemplo de uso: hook pode ser consumido para verificar permissões e renderizar componentes condicionalmente
  return {
    entidade: xEntidade,
    tiposAcesso: xTipoAcesso,
    acessosKeys: xAcessosKeys,
    getAcessos,
    canAcessar,
    hasAlgumAcesso,
    podeEditar,
    podeAprovar,
    podeConsultar,
    podeGerenciar,
    podeConfigurar,
    podeEditarAprovar,
    podeGerenciarAprovar,
    podeEditarGerenciarAprovar,
    podeConsultarEditarAprovar,
    podeConsultarEditarGerenciarAprovar,
    podeConsultarEditarGerenciarConfigurarAprovar,
  };
}

export default useAcesso;
